import React, { useEffect, useState, useRef } from "react";
import Layout from "../components/Layout";
import client from "../lib/api";
import { Panel, Spinner } from "../components/ui-kit";
import { Plus, X, Trash2, Pencil, Eye, EyeOff, Copy, ExternalLink, Upload, Loader2, Users } from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white focus:border-gold focus:outline-none";
const lblCls = "block text-xs text-slate-400 mb-1";
const BLANK = { name: "", login_url: "", login_id: "", password: "", notes: "", id: null };

const Stat = ({ label, value, accent, testid }) => (
  <div className="surface px-4 py-3" data-testid={testid}>
    <div className={`font-head text-2xl font-bold ${accent}`}>{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">{label}</div>
  </div>
);

const Metric = ({ label, value }) => (
  <div className="bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2">
    <div className="text-sm font-head font-semibold text-white">{value ?? "—"}</div>
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
  </div>
);

function BrokerCard({ b, onEdit, onDelete, onUploaded }) {
  const [pw, setPw] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const m = b.latest;

  const reveal = async () => {
    if (pw) { setPw(null); return; }
    try { const { data } = await client.get(`/brokers-vault/${b.id}/password`); setPw(data.password); }
    catch { toast.error("Failed"); }
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("media", file);
      await client.post(`/brokers-vault/${b.id}/report`, fd);
      toast.success("Report parsed ✓"); onUploaded();
    } catch (err) { toast.error(err.response?.data?.detail || "Parse failed"); }
    setUploading(false); if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="surface p-4" data-testid={`broker-${b.id}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-head font-bold text-white text-lg">{b.name}</span>
            {b.login_url && <a href={b.login_url} target="_blank" rel="noreferrer" data-testid={`broker-open-${b.id}`} className="text-gold hover:text-gold/80 inline-flex items-center gap-1 text-xs"><ExternalLink size={13} /> Login</a>}
          </div>
          <div className="text-xs text-slate-400 mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
            {b.login_id && <span>ID: <span className="text-slate-200">{b.login_id}</span></span>}
            <span className="inline-flex items-center gap-1">PW: {pw ? <span className="text-slate-200 font-mono">{pw}</span> : <span className="text-slate-600">••••••••</span>}
              {b.has_password && <button onClick={reveal} data-testid={`broker-reveal-${b.id}`} className="text-slate-500 hover:text-gold">{pw ? <EyeOff size={13} /> : <Eye size={13} />}</button>}
              {pw && <button onClick={() => { navigator.clipboard.writeText(pw); toast.success("Copied"); }} className="text-slate-500 hover:text-gold"><Copy size={12} /></button>}
            </span>
          </div>
          {b.notes && <div className="text-[11px] text-slate-500 mt-1">{b.notes}</div>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,.pdf" onChange={upload} className="hidden" data-testid={`broker-file-${b.id}`} />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`broker-upload-${b.id}`} className="text-xs btn-gold px-2.5 py-1.5 flex items-center gap-1 disabled:opacity-60">
            {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {uploading ? "Parsing…" : "Upload report"}
          </button>
          <button onClick={() => onEdit(b)} data-testid={`broker-edit-${b.id}`} className="text-slate-400 hover:text-white p-1"><Pencil size={15} /></button>
          <button onClick={() => onDelete(b.id)} data-testid={`broker-del-${b.id}`} className="text-slate-400 hover:text-red-400 p-1"><Trash2 size={15} /></button>
        </div>
      </div>

      {m && (
        <div className="mt-4 border-t border-[color:var(--border-subtle)] pt-3" data-testid={`broker-metrics-${b.id}`}>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            <Metric label="Opened today" value={m.accounts_opened_today} />
            <Metric label={`Commission (${m.currency || "USD"})`} value={m.total_commission} />
            <Metric label="Lots" value={m.total_lots} />
            <Metric label="Active" value={m.active_accounts} />
            <Metric label="Inactive" value={m.inactive_accounts} />
          </div>
          {m.summary && <p className="text-xs text-slate-400 mt-2 italic">{m.summary}</p>}
          {Array.isArray(m.top_clients) && m.top_clients.length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1"><Users size={11} /> Most traded clients</div>
              <div className="divide-y divide-[color:var(--border-subtle)]">
                {m.top_clients.map((c, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 text-sm">
                    <span className="text-slate-200">{c.name || c.account || "—"}</span>
                    <span className="text-slate-400 text-xs">{c.lots != null ? `${c.lots} lots` : ""}{c.commission != null ? ` · ${c.commission}` : ""}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="text-[10px] text-slate-600 mt-2">Last report: {b.latest_at ? new Date(b.latest_at).toLocaleString() : "—"}</div>
        </div>
      )}
    </div>
  );
}

export default function BrokerVault() {
  const [brokers, setBrokers] = useState(null);
  const [summary, setSummary] = useState({});
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);

  const load = () => Promise.all([
    client.get("/brokers-vault").then(({ data }) => setBrokers(data)),
    client.get("/brokers-vault-summary").then(({ data }) => setSummary(data)),
  ]);
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (form.id) await client.patch(`/brokers-vault/${form.id}`, form);
      else await client.post("/brokers-vault", form);
      toast.success(form.id ? "Updated" : "Broker added"); setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this broker and its reports?")) return; try { await client.delete(`/brokers-vault/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); } };

  if (!brokers) return <Layout title="Broker Reports"><Spinner /></Layout>;

  return (
    <Layout title="Broker Reports">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-400 max-w-2xl">Store each broker's login (encrypted) and upload their IB/partner report (CSV, Excel or PDF). AI extracts accounts opened, commission, status & top clients.</p>
        <button onClick={() => { setForm(BLANK); setEditing("new"); }} data-testid="bv-add" className="btn-gold px-4 py-2 text-sm flex items-center gap-2 shrink-0"><Plus size={15} /> Add Broker</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Stat testid="bv-stat-today" label="Accounts Opened Today" value={summary.accounts_opened_today ?? 0} accent="text-gold" />
        <Stat testid="bv-stat-commission" label="Total Commission" value={summary.total_commission ?? 0} accent="text-emerald-400" />
        <Stat testid="bv-stat-active" label="Active Accounts" value={summary.active_accounts ?? 0} accent="text-white" />
        <Stat testid="bv-stat-brokers" label="Brokers (reported)" value={`${summary.reported ?? 0}/${summary.brokers ?? 0}`} accent="text-white" />
      </div>

      {brokers.length === 0 ? (
        <Panel><div className="p-6 text-sm text-slate-500">No brokers yet. Add your first broker to store login & upload reports.</div></Panel>
      ) : (
        <div className="space-y-3">
          {brokers.map((b) => <BrokerCard key={b.id} b={b} onEdit={(x) => { setForm({ ...BLANK, ...x, password: "" }); setEditing(x.id); }} onDelete={del} onUploaded={load} />)}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setEditing(null)}>
          <form onSubmit={save} className="surface max-w-md w-full p-5 relative" onClick={(e) => e.stopPropagation()} data-testid="bv-form">
            <button type="button" onClick={() => setEditing(null)} className="absolute top-3 right-3 text-slate-400 hover:text-white"><X size={20} /></button>
            <h3 className="font-head text-lg font-semibold text-white mb-4">{form.id ? "Edit broker" : "Add broker"}</h3>
            <div className="space-y-3">
              <div><label className={lblCls}>Broker name *</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="bv-name" className={inputCls} placeholder="e.g. Vantage" /></div>
              <div><label className={lblCls}>Login link</label><input value={form.login_url} onChange={(e) => setForm({ ...form, login_url: e.target.value })} data-testid="bv-url" className={inputCls} placeholder="https://partners.broker.com" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={lblCls}>Login ID</label><input value={form.login_id} onChange={(e) => setForm({ ...form, login_id: e.target.value })} data-testid="bv-id" className={inputCls} /></div>
                <div><label className={lblCls}>Password {form.id && <span className="text-slate-600">(blank = keep)</span>}</label><input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="bv-pw" className={inputCls} /></div>
              </div>
              <div><label className={lblCls}>Notes</label><input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="bv-notes" className={inputCls} /></div>
            </div>
            <button type="submit" data-testid="bv-save" className="btn-gold w-full py-2.5 text-sm mt-4">{form.id ? "Save changes" : "Add broker"}</button>
          </form>
        </div>
      )}
    </Layout>
  );
}
